import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Typography,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  Divider,
  CircularProgress
} from '@mui/material';
import { Link as LinkIcon, Search } from '@mui/icons-material';
import OverflowTooltip from '../../shared/OverflowTooltip';

const LinkTicketModal = ({ 
  open, 
  onClose, 
  primaryTicket, 
  relationshipTypes = [], 
  onLinkTickets,
  searchTickets,
  loading = false,
  editMode = false,
  initialData = null,
  existingLinkedTickets = [],
  currentTicketId = null
}) => {
  const { t } = useTranslation();
  const [selectedRelationType, setSelectedRelationType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [suggestedTickets, setSuggestedTickets] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchTicketsRef = useRef(searchTickets);

  // Keep searchTickets ref updated
  searchTicketsRef.current = searchTickets;

  // Handle modal open/close and initialization
  useEffect(() => {
    if (!open) return;

    // Initialize form data
    if (editMode && initialData) {
      const matchingRelType = relationshipTypes.find(rt => rt.label === initialData.relationshipType);
      setSelectedRelationType(matchingRelType?.value || initialData.relationshipType || '');
      setSelectedTickets(initialData.targetTicket ? [initialData.targetTicket] : []);
    } else {
      setSelectedRelationType('');
      setSelectedTickets([]);
    }
    
    setSearchTerm('');
    setSearchResults([]);
    
    // Load suggested tickets
    (async () => {
      setLoadingSuggested(true);
      try {
        const results = await searchTicketsRef.current('');
        setSuggestedTickets(results || []);
      } catch (error) {
        console.error('Failed to load suggested tickets:', error);
        setSuggestedTickets([]);
      }
      setLoadingSuggested(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle search with debounce
  useEffect(() => {
    if (searchTerm.length < 1) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const delayedSearch = setTimeout(async () => {
      try {
        const results = await searchTicketsRef.current(searchTerm);
        setSearchResults(results || []);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const handleTicketSelect = (ticket) => {
    if (editMode) {
      // In edit mode, replace current selection
      setSelectedTickets([ticket]);
    } else {
      // In create mode, add if not already selected
      if (!selectedTickets.some(t => t.id === ticket.id)) {
        setSelectedTickets([...selectedTickets, ticket]);
      }
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleTicketRemove = (ticketId) => {
    setSelectedTickets(selectedTickets.filter(t => t.id !== ticketId));
  };

  const isTicketAlreadyLinked = (ticket) => {
    // In edit mode, exclude the ticket being edited from "already linked" check
    if (editMode && initialData?.targetTicket?.id === ticket.id) {
      return false;
    }
    
    // Check against existing linked tickets from the current relationship type
    if ((existingLinkedTickets || []).length > 0 && selectedRelationType) {
      // Find the relationship type label from the selected value
      const selectedRelationshipLabel = relationshipTypes.find(rt => rt.value === selectedRelationType)?.label;
      
      // Check if ticket is already linked in the selected relationship type
      return (existingLinkedTickets || []).some(linkedTicket => 
        linkedTicket.id === ticket.id && 
        (linkedTicket.relationshipType === selectedRelationshipLabel || 
         linkedTicket.relationship_type === selectedRelationType)
      );
    }
    
    return false;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !selectedRelationType || selectedTickets.length === 0) return;
    
    
    // Check for duplicate ticket-relationship combinations
    const selectedRelationshipLabel = (relationshipTypes || []).find(rt => rt.value === selectedRelationType)?.label;
    for (const ticket of selectedTickets) {
      const isDuplicate = (existingLinkedTickets || []).some(linkedTicket => 
        linkedTicket.id === ticket.id && 
        (linkedTicket.relationshipType === selectedRelationshipLabel || 
         linkedTicket.relationship_type === selectedRelationType)
      );
      
      if (isDuplicate) {
        toast.error(t('links.errors.duplicateRelationship', { 
          ticketNumber: ticket.number, 
          relationshipType: selectedRelationshipLabel 
        }));
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      if (editMode) {
        await onLinkTickets({
          relationshipType: selectedRelationType,
          targetTicketId: selectedTickets[0].id,
          target_ticket: currentTicketId
        });
      } else {
        await onLinkTickets({
          relationshipType: selectedRelationType,
          targetTickets: selectedTickets
        });
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if current selection matches initial data (no changes made)
  const initialRelationshipTypeValue = editMode && initialData ? 
    relationshipTypes.find(rt => rt.label === initialData.relationshipType)?.value || initialData.relationshipType
    : null;
  
  const noChanges = editMode && initialData && 
    selectedRelationType === initialRelationshipTypeValue &&
    selectedTickets[0]?.id === initialData.targetTicket?.id;
  
  const isSubmitDisabled = !selectedRelationType || selectedTickets.length === 0 || loading || isSubmitting || noChanges;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            {editMode ? t('links.modal.editLink') : t('links.modal.linkTickets')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* Primary Ticket Display */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('links.modal.linkingTo')}
          </Typography>
          <OverflowTooltip 
            title={`${primaryTicket?.number} · ${primaryTicket?.title}`}
            tooltipWidth={600}
            variant="body1"
            fontWeight="600"
            maxLines={2}
            placement="top-start"
            sx={{ 
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4
            }}
          />
        </Box>

        {/* Relationship Type Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('links.modal.relationshipType')}</InputLabel>
          <Select
            value={selectedRelationType}
            onChange={(e) => setSelectedRelationType(e.target.value)}
            label={t('links.modal.relationshipType')}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  overflow: 'auto'
                }
              }
            }}
          >
            {(relationshipTypes || []).map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Chip
                  label={type.label}
                  size="small"
                  sx={{
                    bgcolor: type.colour || '#1976d2',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Target Tickets Search */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {editMode ? t('links.modal.selectTargetTicket') : t('links.modal.searchSelectTargets')}
          </Typography>
          
          
          {/* Selected Tickets Chips */}
          {selectedTickets.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {(selectedTickets || []).map((ticket) => (
                <Chip
                  key={ticket.id}
                  label={`${ticket.number} · ${ticket.title}`}
                  onDelete={() => handleTicketRemove(ticket.id)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          <TextField
            fullWidth
            placeholder={t('links.modal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            variant="outlined"
          />
        </Box>

        {/* Search Results */}
        {searchTerm.length >= 1 && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {searching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : searchResults.length > 0 ? (
              <List dense>
                {(searchResults || []).map((ticket) => {
                  const alreadySelected = selectedTickets.some(t => t.id === ticket.id);
                  const alreadyLinked = isTicketAlreadyLinked(ticket);
                  const isSelfLink = currentTicketId && ticket.id === currentTicketId;
                  const isDisabled = alreadySelected || alreadyLinked || isSelfLink;
                  
                  const statusMessage = alreadySelected
                    ? t('links.modal.alreadySelected')
                    : alreadyLinked
                    ? t('links.modal.alreadyLinked')
                    : isSelfLink
                    ? t('links.modal.cannotLinkToSelf')
                    : '';
                  
                  return (
                    <ListItem
                      key={ticket.id}
                      button
                      onClick={() => !isDisabled && handleTicketSelect(ticket)}
                      disabled={isDisabled}
                      sx={{
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? 'default' : 'pointer'
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <OverflowTooltip 
                          title={`${ticket.number} · ${ticket.title}`}
                          tooltipWidth={600}
                          variant="body2"
                          maxLines={2}
                          placement="top-start"
                        />
                        {statusMessage && (
                          <Typography variant="caption" color="text.secondary">
                            {statusMessage}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? t('links.modal.noTicketsFound') : t('links.modal.startTypingToSearch')}
                </Typography>
              </Box>
            )}
          </Box>
        )}


        {/* Suggested Tickets */}
        {searchTerm.length < 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('links.modal.suggestedTickets')}
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {loadingSuggested ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : suggestedTickets.length > 0 ? (
                <List dense>
                  {(suggestedTickets || []).map((ticket) => {
                    const alreadySelected = selectedTickets.some(t => t.id === ticket.id);
                    const alreadyLinked = isTicketAlreadyLinked(ticket);
                    const isSelfLink = currentTicketId && ticket.id === currentTicketId;
                    const isDisabled = alreadySelected || alreadyLinked || isSelfLink;
                    
                    const statusMessage = alreadySelected
                      ? t('links.modal.alreadySelected')
                      : alreadyLinked
                      ? t('links.modal.alreadyLinked')
                      : isSelfLink
                      ? t('links.modal.cannotLinkToSelf')
                      : '';
                    
                    return (
                      <ListItem
                        key={ticket.id}
                        button
                        onClick={() => !isDisabled && handleTicketSelect(ticket)}
                        disabled={isDisabled}
                        sx={{ 
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'default' : 'pointer'
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <OverflowTooltip 
                            title={`${ticket.number} · ${ticket.title}`}
                            tooltipWidth={600}
                            variant="body2"
                            maxLines={2}
                            placement="top-start"
                          />
                          {statusMessage && (
                            <Typography variant="caption" color="text.secondary">
                              {statusMessage}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('links.modal.noSuggestedTickets')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
          {t('links.modal.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isSubmitDisabled}
        >
          {editMode ? t('links.modal.updateLink') : t('links.modal.linkTickets')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkTicketModal;